(ns common.widgets.sidebar
  "Reusable parts of collapsible and resizable panel/sidebar.

  Uses by common.widebar.view and others."
  (:require ["@mui/material/Chip$default" :as Chip]
            ["@mui/material/Drawer$default" :as Drawer]
            ["@mui/material/Fab$default" :as Fab]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/Tooltip$default" :as Tooltip]
            ["@mui/material/styles" :as styles]
            ["react" :as react]
            ["react-transition-group" :refer [Transition]]
            ["react-use/lib/useKey$default" :as useKey]
            [common.util :as util]
            [common.widgets.resize :as resize]
            [uix.core :as uix :refer [$ defui]]))

(defn write-sidebar-toggle [k open]
  (js/localStorage.setItem k (js/JSON.stringify open)))

;; Some sidebar content might need to know if the sidebar is collapsed or not.
;; Share it with Context so we don't need pass it through every component.
(def SidebarContext (react/createContext {:open false}))
(def SidebarContextProvider (.-Provider SidebarContext))

(defn transition [^js theme state property]
  (case state
    "entering" (.. theme -transitions (create property #js {:easing (.. theme -transitions -easing -sharp)
                                                            :duration (.. theme -transitions -duration -enteringScreen)}))
    "exiting"  (.. theme -transitions (create property #js {:easing (.. theme -transitions -easing -sharp)
                                                            :duration (.. theme -transitions -duration -leavingScreen)}))
    nil))

(defn read-sidebar-toggle [k]
  (let [v (js/JSON.parse (js/localStorage.getItem k))]
    (if (some? v)
      v
      true)))

(defn use-sidebar [{:keys [toggle-key toggle-button? width-key default-width min-width hotkey
                           open-state]}]
  (let [;; Alternative open state store
        [open set-open _remove] (if open-state
                                  open-state
                                  (uix/use-state (fn initialize-state []
                                                   (read-sidebar-toggle toggle-key))))
        [width set-width] (resize/use-drawer-width {:k width-key
                                                    :default-width default-width
                                                    ;; Will trigger collapse
                                                    :min-width min-width})
        ;; When the resize is done (mouse-up) check if the width
        ;; is below default width and trigger collapse.
        ;; Set the width back to 240 so expanding the panel will use
        ;; the default width.
        on-resize-done (uix/use-callback (fn [width]
                                           (when (< width default-width)
                                             (set-width default-width)
                                             (when toggle-button?
                                               (set-open false))))
                                         ;; set-open should be stable
                                         ^:lint/disable
                                         [set-width default-width])
        [hover set-hover] (uix/use-state false)]
    ;; Be careful to now change the hotkey option between renders
    (when hotkey
      (useKey hotkey #(set-open not)))
    (uix/use-effect (fn []
                      (write-sidebar-toggle toggle-key open)
                      js/undefined)
                    [toggle-key open])
    {:open open
     :set-open set-open
     :width width
     :set-width set-width
     :on-resize-done on-resize-done
     :hover hover
     :set-hover set-hover}))

(defn sidebar-sx [theme state open hover width anchor paper-sx]
  #js {:whiteSpace "nowrap"
       :boxSizing "border-box"
       :position "relative"
       ;; Set max width so resize can't go over 50% viewport width
       :maxWidth "50%"
       ;; Use the resize width if open and only set the width based on transition status
       :width (if open
                (case state
                  ("entering" "entered") width
                  24)
                24)

       ;; Collapse/expand transition - enabled only when the collapse state changes.
       ;; If transition was enabled for resize, the perf would be bad.
       ;; This transition affects other layout, siblings etc.
       :transition (transition theme state "width")

       ;; Show the panel on drawer hover
       "& > .MuiPaper-root" (util/spread-obj
                             #js {;; The toggle button is partially outside of the Paper, so need to allow overflow.
                                  :overflow "unset"
                                  :zIndex 650

                                  ;; Set absolute position if collapsed but hovering
                                  :position (if (and (not open) hover)
                                              "absolute"
                                              "relative")
                                  ;; The width is 100% when panel is expanded, but controlled
                                  ;; when it is collapsed. Use the saved resize width on hover.
                                  :width (case state
                                           ("entering" "entered") (if hover
                                                                    width
                                                                    "100%")
                                           24)
                                  ;; Positioning for hover, doesn't affect expanded state.
                                  :left (when (= "left" anchor) 0)
                                  :right (when (= "right" anchor) 0)
                                  :height "100%"

                                  ;; Hover width transition
                                  ;; also content width transition (affect sidebar border) for toggle
                                  :transition (transition theme state "width")}
                             paper-sx)
       "& > .MuiPaper-root > .MuiStack-root" (when-not open
                                               #js {;; Also make the contents transparent because they would
                                                    ;; show up tiny bit even on 24px drawer.
                                                    ;; Add transition so they fade into/out nicely.
                                                    :opacity (case state
                                                               ("entering" "entered") 1
                                                               0)
                                                    :transition (transition theme state "opacity")})

       ;; Toggle button
       ;; Display the toggle button always when collapsed and on hover when expanded.
       "& .MuiFab-root" #js {:opacity (if open 0 1)}
       "&:hover .MuiFab-root" #js {:opacity 1}})

(defui open-toggle-button [{:keys [ref open set-open placement hotkey]
                            :or {placement "right"
                                 hotkey "["}}]
  ($ Tooltip
     {:title ($ :<>
                (if open
                  "Collapse"
                  "Expand")
                (when hotkey
                  ($ Chip
                     {:label hotkey
                      :sx #js {:ml 1
                               :color "white"
                               :backgroundColor "rgba(255, 255, 255, 0.25)"}})))
      :placement placement
      :arrow false
      ;; Tooltip hover breaks drawer mouseOver
      :disableInteractive true}
     ($ Fab {:ref ref
             :size "small"
             :on-click #(set-open not)
             :sx #js {:position "absolute"
                      ;; This is close to being center to the 1px sidebar border
                      ;; This isn't centered vs. the resize border
                      :right (when (= "right" placement) 0)
                      :left (when (= "left" placement) 0)
                      :top "1rem"
                      :transform (if (= "left" placement)
                                   "translate(-50%, 0%)"
                                   "translate(50%, 0%)")
                      :width "24px"
                      :height "24px"
                      :minHeight "unset"
                      :backgroundColor "white"
                      "&:hover" #js {:backgroundColor "primary.main"
                                     :color "white"}}
             :onMouseOver (fn [e]
                            ;; Prever onMouseOver for drawer when hovering the toggle btn
                            (.stopPropagation e))}
        ($ Icon
           {:sx (fn [^js theme]
                  #js {:transform (if (= "left" placement)
                                    (if open
                                      "rotate(180deg)"
                                      "rotate(0deg)")
                                    (if open
                                      "rotate(0deg)"
                                      "rotate(180deg)"))
                       :transition (.. theme -transitions (create "transform" #js {:easing (.. theme -transitions -easing -sharp)
                                                                                   :duration (.. theme -transitions -duration -enteringScreen)}))
                       :fontSize "xl"})}
           "keyboard_arrow_left"))))

(defui sidebar
  "If you need to customize this, it is also fine to reimplement this component
  and use the other functions from the ns."
  [{:keys [children hotkey drawer-anchor toggle-placement paper-sx toggle-button?] :as options
    :or {toggle-button? true}}]
  (let [theme (styles/useTheme)
        {:keys [open set-open hover set-hover width set-width on-resize-done]}
        (use-sidebar options)

        ctx (uix/use-memo (fn []
                            {:open open})
                          ;; set-open is stable
                          ^:lint/disable
                          [open])]
    ($ SidebarContextProvider
       {:value ctx}
       ($ Transition
          {:in (or hover open)
           :timeout #js {:enter (.. theme -transitions -duration -enteringScreen)
                         :exit (.. theme -transitions -duration -leavingScreen)}}
          (fn [state]
            ($ Drawer
               {:open open
                :variant "permanent"
                :anchor drawer-anchor
                :onMouseOver  (when-not open (fn [_e] (set-hover true)))
                :onMouseLeave (when-not open (fn [_e] (set-hover false)))
                :sx (fn [theme]
                      (sidebar-sx theme state open hover width drawer-anchor paper-sx))}

               (when toggle-button?
                 ($ open-toggle-button
                    {:open open
                     :set-open (fn [v]
                                 ;; Also se hover false so that the panel closes even when the toggle btn is hovered
                                 (set-hover false)
                                 (set-open v))
                     :hotkey hotkey
                     :placement toggle-placement}))

               ;; NOTE: What if content needs the transition state?
               children

               (when open
                 ($ resize/resize-handle
                    {:width width
                     :set-width set-width
                     :calculate-width (if (= "right" drawer-anchor)
                                        resize/right-side-width
                                        resize/left-side-width)
                     ;; Place the resize handle over the sidebar border
                     :sx (if (= "right" drawer-anchor)
                           #js {:left 0}
                           #js {:right -1})
                     :border-sx (if (= "right" drawer-anchor)
                                  #js {:left -2}
                                  #js {:right -1})
                     :on-done on-resize-done}))))))))
